import {
  Group,
  MultiSelect,
  Select,
  Stack,
  Text,
} from "@mantine/core"
import { PackageIcon, TruckIcon, UserIcon } from "@phosphor-icons/react"
import { serverOptionsFilter } from "@dehub/theme"
import { useTranslation } from "react-i18next"
import { StatusDot, WizardSection } from "./WizardSection"

interface Props {
  vehicleOptions   : Array<{ value: string; label: string }>
  vehiclesLoading  : boolean
  vehicleId        : string | null
  onVehicleChange  : (id: string | null) => void

  driverOptions    : Array<{ value: string; label: string }>
  driversLoading   : boolean
  driverId         : string | null
  onDriverChange   : (id: string | null) => void
  onDriverSearch   : (query: string) => void
  driverDisabled   : boolean

  pullSheetStaffOptions : Array<{ value: string; label: string }>
  pullSheetStaffLoading : boolean
  pullSheetStaffIds     : string[]
  onPullSheetStaffChange: (ids: string[]) => void
  onPullSheetStaffSearch: (query: string) => void

  isComplete       : boolean
}

export function AssignmentSection({
  vehicleOptions,
  vehiclesLoading,
  vehicleId,
  onVehicleChange,
  driverOptions,
  driversLoading,
  driverId,
  onDriverChange,
  onDriverSearch,
  driverDisabled,
  pullSheetStaffOptions,
  pullSheetStaffLoading,
  pullSheetStaffIds,
  onPullSheetStaffChange,
  onPullSheetStaffSearch,
  isComplete,
}: Props) {
  const { t } = useTranslation(["delivery"])

  return (
    <WizardSection n={4} title={t("delivery:route.wizard.step4")} aside={isComplete ? <StatusDot tone="green">{t("delivery:route.wizard.filled")}</StatusDot> : null}>
        <Stack gap="md">
          <Group grow align="flex-start">
            <Select
              label={t("delivery:route.card.vehicle")}
              placeholder={vehiclesLoading ? t("delivery:route.wizard.loading") : t("delivery:route.wizard.vehiclePick")}
              data={vehicleOptions}
              value={vehicleId}
              onChange={onVehicleChange}
              clearable
              searchable
              disabled={vehiclesLoading}
              leftSection={<TruckIcon size={14} />}
              nothingFoundMessage={t("delivery:route.wizard.vehicleNone")}
            />
            <Select
              label={t("delivery:route.card.driver")}
              placeholder={driverDisabled ? t("delivery:route.wizard.noBusiness") : t("delivery:route.wizard.driverPick")}
              data={driverOptions}
              value={driverId}
              onChange={onDriverChange}
              onSearchChange={onDriverSearch}
              clearable
              searchable
              // The server already decided what matches; without this Mantine
              // re-filters its answer against the label and drops every row
              // whose script differs from what was typed.
              filter={serverOptionsFilter}
              disabled={driverDisabled}
              leftSection={<UserIcon size={14} />}
              nothingFoundMessage={driversLoading ? t("delivery:route.wizard.searching") : t("delivery:route.wizard.driverNone")}
            />
          </Group>
          <MultiSelect
            label={t("delivery:route.detail.loader")}
            description={t("delivery:route.wizard.loaderHint")}
            inputWrapperOrder={["label", "input", "description", "error"]}
            placeholder={
              driverDisabled
                ? t("delivery:route.wizard.noBusiness")
                : pullSheetStaffIds.length > 0
                  ? t("delivery:route.wizard.loaderAdd")
                  : t("delivery:route.wizard.loaderPick")
            }
            data={pullSheetStaffOptions}
            value={pullSheetStaffIds}
            onChange={onPullSheetStaffChange}
            onSearchChange={onPullSheetStaffSearch}
            clearable
            searchable
            filter={serverOptionsFilter}
            hidePickedOptions
            disabled={driverDisabled}
            leftSection={<PackageIcon size={14} />}
            nothingFoundMessage={pullSheetStaffLoading ? t("delivery:route.wizard.searching") : t("delivery:route.wizard.loaderNone")}
          />
          <Text size="sm" c="gray.7">
            {t("delivery:route.wizard.assignHint")}
          </Text>
        </Stack>
    </WizardSection>
  )
}
